import { ComplexBundle } from "../types/Product";
import {
  Selections,
  shouldEntryBeShown,
  shouldStepBeShown,
} from "./conditions.ts";

export type BuilderMode = "new" | "editing";

/**
 * Disabled steps still render in order-edit mode so historical orders stay resolvable.
 * Role-hidden content stays excluded in both modes. Conditional visibility
 * is dynamic and applies in both modes, matching the server cart routes.
 */
export const shouldUseStep = (
  step: ComplexBundle.Step,
  mode: BuilderMode,
  activeSelections: Selections = {},
): boolean =>
  step.step_role_hidden !== "on" &&
  (mode === "editing" || step.step_disabled !== "on") &&
  shouldStepBeShown(step, activeSelections);

export const shouldUseEntry = (
  entry: ComplexBundle.Entry,
  mode: BuilderMode,
  activeSelections: Selections,
): boolean =>
  entry.entry_role_hidden !== "on" &&
  (mode === "editing" || entry.entry_disabled !== "on") &&
  shouldEntryBeShown(entry, activeSelections);

/**
 * Step conditions may only reference earlier steps, and field conditions may reference
 * earlier steps or earlier fields within their own step, so one ordered pass is
 * deterministic: every step and field is evaluated against the selections accepted before
 * it, and a hidden step or field's selections cannot satisfy anything later. Must stay in
 * sync with BundleDataParser::get_bundle_cart_data.
 */
export const activeStructure = (
  steps: ComplexBundle.Step[],
  selections: Selections,
  mode: BuilderMode,
): {
  visibleSteps: Set<number>;
  visibleEntries: Set<string>;
  activeSelections: Selections;
} => {
  const visibleSteps = new Set<number>();
  const visibleEntries = new Set<string>();
  // Start from every selection and drop the hidden ones as we walk, so a field can be
  // conditional on an earlier field in its own step. Building the set up instead would
  // hide a step's own answers from its later fields.
  const activeSelections: Selections = { ...selections };

  const dropSelectionsUnder = (prefix: string) => {
    for (const key of Object.keys(activeSelections)) {
      if (key === prefix || key.startsWith(`${prefix}-`)) {
        delete activeSelections[key];
      }
    }
  };

  steps.forEach((step, stepIndex) => {
    if (!shouldUseStep(step, mode, activeSelections)) {
      dropSelectionsUnder(`${stepIndex}`);
      return;
    }
    visibleSteps.add(stepIndex);

    step.entries.forEach((entry, entryIndex) => {
      const entryKey = `${stepIndex}-${entryIndex}`;
      if (!shouldUseEntry(entry, mode, activeSelections)) {
        dropSelectionsUnder(entryKey);
        return;
      }
      visibleEntries.add(entryKey);
    });
  });

  return { visibleSteps, visibleEntries, activeSelections };
};

export const visibleStepIndexes = (
  steps: ComplexBundle.Step[],
  selections: Selections,
  mode: BuilderMode,
): Set<number> => activeStructure(steps, selections, mode).visibleSteps;

export const selectionsForActiveSteps = (
  steps: ComplexBundle.Step[],
  selections: Selections,
  mode: BuilderMode,
): Selections => activeStructure(steps, selections, mode).activeSelections;
